import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RegistrationGroup } from "../../api/getEventTransactionDetails/types";
import { format } from "date-fns";
import { ChevronDown, ChevronRight } from "lucide-react";
import { TransactionDetails } from "./TransactionDetails";

interface TransactionListProps {
  registrationGroups: RegistrationGroup[];
  onSelectGroup: (group: RegistrationGroup) => void;
  selectedGroupId: string | null;
  onRefundSuccess: () => void;
}

export function TransactionList({
  registrationGroups,
  onSelectGroup,
  selectedGroupId,
  onRefundSuccess,
}: TransactionListProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (group: RegistrationGroup) => {
    if (group.hasPendingRefunds) {
      return <Badge variant="outline">Pending Refund</Badge>;
    }
    if (group.refundedAmount > 0) {
      return <Badge variant="secondary">Partially Refunded</Badge>;
    }
    return <Badge variant="default">Paid</Badge>;
  };

  if (registrationGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">No transactions found</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]"></TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Attendees</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {registrationGroups.map((group) => {
            const isExpanded = selectedGroupId === group.id;

            return (
              <>
                <TableRow
                  key={group.id}
                  onClick={() => onSelectGroup(group)}
                  className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                    isExpanded ? "bg-muted/50" : ""
                  }`}
                >
                  <TableCell>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {group.companyName}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(group.createdAt)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 text-sm">
                      <span>{group.actualAttendeeCount} total</span>
                      {group.voucherCount > 0 && (
                        <span className="text-muted-foreground">
                          ({group.voucherCount} voucher)
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(group.totalPaid)}
                  </TableCell>
                  <TableCell>{getStatusBadge(group)}</TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow key={`${group.id}-details`}>
                    <TableCell colSpan={6} className="p-0">
                      <TransactionDetails
                        group={group}
                        onRefundSuccess={onRefundSuccess}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
